import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Loader from "../../components/Loader.jsx";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FaGripLines } from "react-icons/fa6";
import {
  getCustomCategoryByID,
  getProductCategoryAssignment,
  reorderFeaturedProductsInCustomCategory,
} from "../../api/inventory.js";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import {
  InstantSearch,
  Configure,
  useHits,
  useSearchBox,
} from "react-instantsearch-hooks";

import config from '@/config';
import { useSelector } from "react-redux";
import Autocomplete from "../products/autocomplete.jsx";
import { FaHeart, FaRegHeart, FaRegStar, FaStar, FaHeartCrack } from "react-icons/fa6";

const CategoryProducts = ({ categoryID, allCategories }) => {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [inputBoxDom, setInputBoxDom] = useState(null);
  const { selectedMerchant } = useSelector((state) => state.app);
  const [typesenseInstantsearchAdapter] = useState(
    new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.ts.key, // Be sure to use an API key that only allows search operations
        nodes: config.ts.nodes,
        nearestNode: config.ts.nearestNode,
        cacheSearchResultsForSeconds: 2 * 60, // Cache search results from server. Defaults to 2 minutes. Set to 0 to disable caching.
      },
      // The following parameters are directly passed to Typesense's search API endpoint.
      //  So you can pass any parameters supported by the search endpoint below.
      //  query_by is required.
      additionalSearchParameters: {
        sort_by: "ordinal:asc,title:asc",
        query_by: "title,subtitle,default_category,description,company",
      },
    })
  );
  const searchClient = useMemo(() => {
    if (!typesenseInstantsearchAdapter.searchClient) return null;
    return typesenseInstantsearchAdapter.searchClient;
  }, [typesenseInstantsearchAdapter]);

  const autoCompleteRef = useCallback((node) => {
    if (node) {
      setTimeout(() => {
        setInputBoxDom(node.getElementsByTagName("input")[0]);
      }, 250);
    }
  }, []);

  const { currentlyFeaturedProducts, currentlyFeaturedSkus } = useMemo(() => {
    const cfp = products.filter((p) => p.ordinal < 1000);
    cfp.sort((a, b) => a.ordinal - b.ordinal);
    return {
      currentlyFeaturedProducts: cfp,
      currentlyFeaturedSkus: cfp.map((p) => p.sku_id),
    };
  }, [products]);

  const addToFeatured = (sku_id) => {
    const newProducts = products.map((p) => {
      if (p.sku_id === sku_id) {
        p.ordinal = currentlyFeaturedProducts.length;
      }
      return p;
    });
    setProducts(newProducts);
    submitRearrange(newProducts);
  };

  const submitRearrange = async (list) => {
    // create map of {sku_id: ordinal}
    const feat = list.filter((item) => item.ordinal < 1000);
    feat.sort((a, b) => a.ordinal - b.ordinal);
    feat.forEach((item, index) => {
      item.ordinal = index + 1;
    });
    const ordinals = {};
    feat.forEach((item, index) => {
      ordinals[item.sku_id] = index;
    });
    reorderFeaturedProductsInCustomCategory(category._id, ordinals);
  };

  const removeFromFeatured = (sku_id) => {
    const newProducts = products.map((p) => {
      if (p.sku_id === sku_id) {
        p.ordinal = 1000;
      }
      return p;
    });
    setProducts(newProducts);
    submitRearrange(newProducts);
  };

  useEffect(() => {
    if (categoryID) {
      setCategory(null);
      getCustomCategoryByID(categoryID).then((res) => {
        res.products.sort((a, b) => a.ordinal - b.ordinal);
        setCategory(res);
      });
    }
  }, [categoryID]);

  useEffect(() => {
    if (editingProductId) {
      const product = getProductCategoryAssignment(editingProductId);
      product.then((res) => {
        const theProduct = products.find((p) => p.sku_id === editingProductId);
        theProduct.product = theProduct.product ?? theProduct.custom_product;
        setEditingProduct({
          categories: (res.categories || []).map((c) => c._id),
          product: theProduct,
        });
      });
    }
  }, [editingProductId]);

  useEffect(() => {
    if (category) {
      setProducts(category.products);
    } else {
      setProducts([]);
    }
  }, [category]);
  if (!category) return <Loader />;

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const newItems = Array.from(currentlyFeaturedProducts);
    const [removed] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, removed);
    for (let i = 0; i < newItems.length; i++) {
      newItems[i].ordinal = i;
    }

    const newProducts = Array.from(products);
    for (let i = 0; i < newProducts.length; i++) {
      const match = newItems.find((item) => item.sku_id === products[i].sku_id);
      if (match) {
        newProducts[i].ordinal = match.ordinal;
      }
    }

    setProducts(newProducts);
    submitRearrange(newProducts);
  };
  return (
    <div className="mt-4 w-full">
      <ModalBasic
        title="Edit Category Assignment"
        modalOpen={Boolean(editingProductId)}
        setModalOpen={(open) => {
          if (!open) {
            setEditingProductId(null);
            setEditingProduct(null);
          }
        }}
        width="xl"
      >
        {!editingProduct && <Loader />}
        {editingProduct && (
          <div>
            <h2 className="text-xl font-bold mb-4">
              {editingProduct.product.product.title}{" "}
              {editingProduct.product.product.subtitle}
            </h2>
            {allCategories.map((cat) => {
              const checked = editingProduct.categories.includes(cat._id);
              return (
                <div key={cat._id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const newCategories = checked
                        ? editingProduct.categories.filter((c) => c !== cat._id)
                        : [...editingProduct.categories, cat._id];
                      setEditingProduct({
                        ...editingProduct,
                        categories: newCategories,
                      });
                    }}
                  />
                  <span>{cat.name}</span>
                </div>
              );
            })}
            <div className="mt-4 flex gap-2 justify-end">
              <SecondaryButton>Cancel</SecondaryButton>
              <PrimaryButton>Save</PrimaryButton>
            </div>
          </div>
        )}
      </ModalBasic>

      <InstantSearch
        indexName={`${config.environment}_${selectedMerchant.easy_id}_products`}
        searchClient={searchClient}
      >
        <Configure
          hitsPerPage={50}
          filters={`default_category:=${category.name}`}
        />
        <div className="relative">
          <div ref={autoCompleteRef}>
            <Autocomplete
              searchClient={searchClient}
              placeholder="Search to add feature products"
              detachedMediaQuery="none"
              openOnFocus
            />
          </div>

          <div className="text-sm py-2">
            You can only feature products that are in this category. Learn how
            to add products to a category{" "}
            <a target='_blank' href="https://support.saucey.com/en/articles/9627765-custom-categories" className="underline cursor-pointer">
              here
            </a>
            .
          </div>
          <div></div>
          <SearchResults
            currentlyFeaturedSkus={currentlyFeaturedSkus}
            addToFeatured={addToFeatured}
            removeFromFeatured={removeFromFeatured}
            inputBoxDom={inputBoxDom}
          />
        </div>
      </InstantSearch>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {currentlyFeaturedProducts.map((item, index) => {
                const product = item.product ?? item.custom_product;
                return (
                  <Draggable
                    key={item.sku_id}
                    draggableId={item.sku_id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="flex gap-2 items-center  user-select-none p-2 bg-white dark:bg-theme-surface-accent text-black dark:text-white border border-gray-300 dark:border-slate-600"
                        style={{
                          ...provided.draggableProps.style,
                        }}
                      >
                        <FaGripLines className="text-2xl" />
                        <div className="w-10 h-10 shrink-0 mr-2 sm:mr-3">
                          {/*checkbox*/}
                          <img
                            className="rounded-full w-[40px] h-[40px]"
                            src={product.image}
                            width="40"
                            height="40"
                            alt={product.name}
                          />
                        </div>

                        {product.title} {product.subtitle}{" "}
                        {product.volume_label
                          ? ` - ${product.volume_label}`
                          : ""}

                        <span className="text-slate-400">{product.company}</span>
                        <SecondaryButton
                          onClick={() => {
                            removeFromFeatured(item.sku_id);
                          }}
                          title={"Un-feature"}
                        >
                          <FaHeartCrack className="text-slate-700 dark:text-slate-400" />
                        </SecondaryButton>
                      </div>
                    )}
                  </Draggable>
                );
              })}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const SearchResults = ({
  removeFromFeatured,
  addToFeatured,
  currentlyFeaturedSkus = [],
  inputBoxDom,
}) => {
  const { hits } = useHits();
  const { query } = useSearchBox();
  const [show, setShow] = useState(false);
  const resultWrapper = useRef(null);


  const globalClickEventListener = useCallback(
    (e) => {
      if (document.activeElement.className.includes("aa-Input")) {
        setShow(true);
      } else {
        // check if e target is a descendant of the wrapper
        if (resultWrapper.current && !resultWrapper.current.contains(e.target)) {
          setShow(false);
        }
      }
    },
    [inputBoxDom, resultWrapper.current]
  );
  useEffect(() => {
    document.addEventListener("click", globalClickEventListener);
    return () => {
      document.removeEventListener("click", globalClickEventListener);
    };
  }, [inputBoxDom]);

  if (!show) {
    return null;
  }
  const result = () => {
    if (query && hits.length === 0) {
      return (
        <div className="absolute top-[46px] w-full max-h-96 overflow-y-auto bg-white border border-indigo-100 p-2 text-sm">
          No results found for <span className="font-bold">{query}</span>
        </div>
      );
    }
    return (
      <div className="absolute top-[46px] w-full max-h-96 overflow-y-auto bg-white border border-indigo-100">
        {hits.map((hit) => {
          const currentlyFeatured = currentlyFeaturedSkus.includes(hit.sku_id);
          const HeartComp = currentlyFeatured ? FaHeart : FaRegHeart;
          return (
            <div
              className="flex gap-2 items-center user-select-none p-2 bg-white dark:bg-theme-surface-accent text-black"
              key={hit.id}
            >
              <HeartComp
                onClick={(e) => {
                  e.stopPropagation();
                  currentlyFeatured
                    ? removeFromFeatured(hit.sku_id)
                    : addToFeatured(hit.sku_id);
                }}
                className={
                  "cursor-pointer text-lg " +
                  (currentlyFeatured ? "text-red-500" : "text-gray-400")
                }
              />
              {`${hit.title}${hit.subtitle ? ` ${hit.subtitle}` : ""} ${
                hit.volume_label ? hit.volume_label : ""
              }`}
            </div>
          );
        })}
      </div>
    );
  };
  return <div ref={resultWrapper}>{result()}</div>;
};

export default CategoryProducts;
